/**
 * Iter 67u — Vendor "Outstanding balance" panel with TWO payment options.
 *
 * The vendor sees a single "Pay Now" CTA on outstanding invoices. Clicking it
 * opens a method-picker (Stripe Card · Paysera). Each option:
 *   • Stripe → POST `/vendor/billing/pay/stripe` → redirect to Stripe Checkout.
 *     On return (`?stripe_pay_session=`) we poll
 *     `/vendor/billing/pay/stripe/status/{id}` to flip the local state.
 *   • Paysera → POST `/vendor/billing/pay/paysera` → redirect to Paysera-hosted
 *     checkout. Webhook flips the rows.
 *
 * Also surfaces the unified payment-history table (both gateways) with the
 * downloadable PDF receipt link + the Stripe / Paysera receipt URL if present.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import Money from "@/components/Money";
import {
  CreditCard, Wallet, Receipt, Loader2, ExternalLink, CheckCircle2,
  AlertTriangle, Clock, ShieldCheck,
} from "lucide-react";

function _statusPill(s) {
  if (s === "paid")    return <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"><CheckCircle2 className="h-3 w-3" />paid</span>;
  if (s === "failed")  return <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200"><AlertTriangle className="h-3 w-3" />failed</span>;
  if (s === "pending") return <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200"><Loader2 className="h-3 w-3 animate-spin" />pending</span>;
  return <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200"><Clock className="h-3 w-3" />open</span>;
}

export default function VendorPaymentsCard() {
  const [outstanding, setOutstanding] = useState({ invoices: [], total: 0, currency: "EUR" });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [paying, setPaying] = useState(null); // 'stripe' | 'paysera' | null
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    setLoading(true);
    try {
      const [o, h] = await Promise.all([
        api.get("/vendor/billing/outstanding"),
        api.get("/vendor/billing/payments"),
      ]);
      setOutstanding(o.data);
      setHistory(h.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // After-redirect handlers: Stripe one-time pay returns with
  // `?stripe_pay_session=cs_...` — poll the status endpoint until Stripe
  // confirms payment_status='paid', then refresh state.
  useEffect(() => {
    const sessionId = searchParams.get("stripe_pay_session");
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled || attempts >= 6) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/vendor/billing/pay/stripe/status/${sessionId}`);
        if (data?.payment_status === "paid") {
          toast.success("Payment confirmed — invoices marked as paid.");
          await load();
          // Clear the query param so reload doesn't re-poll.
          searchParams.delete("stripe_pay_session");
          setSearchParams(searchParams, { replace: true });
          return;
        }
      } catch (e) {
        // Non-fatal — keep polling.
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  const payVia = async (gateway) => {
    setPaying(gateway);
    try {
      const url = gateway === "stripe"
        ? "/vendor/billing/pay/stripe"
        : "/vendor/billing/pay/paysera";
      const { data } = await api.post(url, {});
      const redirect = data?.payment_url || data?.checkout_url;
      if (redirect) {
        window.location.assign(redirect);
      } else {
        toast.error(`${gateway} didn't return a redirect URL.`);
        setPaying(null);
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setPaying(null);
    }
  };

  return (
    <div className="rounded-3xl border border-indigo-100 bg-gradient-to-br from-sky-50/40 via-white to-violet-50/40 p-6" data-testid="vendor-payments-card">
      <div className="flex items-start gap-3 mb-5">
        <div className="h-11 w-11 rounded-2xl bg-indigo-50 ring-1 ring-indigo-100 flex items-center justify-center shrink-0">
          <Wallet className="h-5 w-5 text-indigo-600" />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-lg font-semibold leading-tight">Outstanding balance</h3>
          <p className="text-sm text-neutral-600 mt-0.5">
            Subscription fees, commission balance, and unpaid invoices — pay by Stripe card or Paysera in one click.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500">Loading…</div>
      ) : outstanding.invoices.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-emerald-200 bg-emerald-50/40 p-6 text-center" data-testid="vendor-payments-empty">
          <CheckCircle2 className="h-7 w-7 text-emerald-600 mx-auto mb-2" />
          <div className="font-semibold text-emerald-800">You're all clear.</div>
          <div className="text-sm text-neutral-600 mt-0.5">No outstanding invoices to pay right now.</div>
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-rose-100 bg-rose-50/40 p-4 mb-4 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-rose-700">Due now</div>
              <div className="font-display text-3xl font-semibold text-neutral-900" data-testid="vendor-payments-total">
                <Money amount={outstanding.total} />
              </div>
              <div className="text-xs text-neutral-500 mt-0.5">
                Across {outstanding.invoices.length} invoice{outstanding.invoices.length === 1 ? "" : "s"}.
              </div>
            </div>
            <Button
              onClick={() => setPickerOpen(true)}
              className="rounded-full h-12 px-6 bg-gradient-to-r from-sky-600 via-indigo-600 to-violet-600 hover:from-sky-700 hover:via-indigo-700 hover:to-violet-700 text-white shadow-lg shadow-indigo-500/20"
              data-testid="vendor-pay-now-btn"
            >
              <CreditCard className="h-4 w-4 mr-2" /> Pay Now
            </Button>
          </div>

          <div className="rounded-2xl border border-border overflow-hidden" data-testid="vendor-payments-open-list">
            <table className="w-full text-xs">
              <thead className="bg-neutral-50 text-left">
                <tr>
                  <th className="px-3 py-2 font-semibold text-neutral-600">Invoice</th>
                  <th className="px-3 py-2 font-semibold text-neutral-600">Period</th>
                  <th className="px-3 py-2 font-semibold text-neutral-600 text-right">Amount</th>
                  <th className="px-3 py-2 font-semibold text-neutral-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {outstanding.invoices.map((inv) => (
                  <tr key={inv.id} className="border-t border-border" data-testid={`vendor-open-invoice-${inv.id}`}>
                    <td className="px-3 py-2 font-mono text-[11px]">{inv.id.slice(0, 8)}</td>
                    <td className="px-3 py-2 text-neutral-700">
                      {(inv.period_start || "—").slice(0, 10)} → {(inv.period_end || "—").slice(0, 10)}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold"><Money amount={inv.amount} /></td>
                    <td className="px-3 py-2">{_statusPill(inv.pending_gateway ? "pending" : inv.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {history.length > 0 && (
        <div className="mt-6" data-testid="vendor-payments-history">
          <h4 className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-2 flex items-center gap-1.5">
            <Receipt className="h-3.5 w-3.5 text-violet-600" /> Payment history
          </h4>
          <div className="rounded-2xl border border-border overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-neutral-50 text-left">
                <tr>
                  <th className="px-3 py-2 font-semibold text-neutral-600">When</th>
                  <th className="px-3 py-2 font-semibold text-neutral-600">Gateway</th>
                  <th className="px-3 py-2 font-semibold text-neutral-600 text-right">Amount</th>
                  <th className="px-3 py-2 font-semibold text-neutral-600">Status</th>
                  <th className="px-3 py-2 font-semibold text-neutral-600 text-right">Receipt</th>
                </tr>
              </thead>
              <tbody>
                {history.map((p) => (
                  <tr key={p.id} className="border-t border-border" data-testid={`vendor-payment-${p.id}`}>
                    <td className="px-3 py-2 text-neutral-700">{(p.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-3 py-2 capitalize">{p.gateway}</td>
                    <td className="px-3 py-2 text-right font-semibold"><Money amount={p.amount} /></td>
                    <td className="px-3 py-2">{_statusPill(p.status)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap space-x-2">
                      {p.receipt_url && (
                        <a href={p.receipt_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-violet-600 hover:underline" data-testid={`vendor-payment-receipt-${p.id}`}>
                          <ExternalLink className="h-3 w-3" /> {p.gateway === "stripe" ? "Stripe" : "Paysera"}
                        </a>
                      )}
                      <a href={`/api/vendor/billing/payments/${p.id}/invoice.pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-indigo-600 hover:underline" data-testid={`vendor-payment-pdf-${p.id}`}>
                        <Receipt className="h-3 w-3" /> PDF
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Method picker */}
      <Dialog open={pickerOpen} onOpenChange={(v) => !paying && setPickerOpen(v)}>
        <DialogContent className="max-w-md" data-testid="pay-method-picker">
          <DialogHeader>
            <DialogTitle>Choose your payment method</DialogTitle>
            <DialogDescription>
              You're settling <Money amount={outstanding.total} /> across {outstanding.invoices.length} invoice{outstanding.invoices.length === 1 ? "" : "s"}.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 mt-2">
            <button
              type="button"
              onClick={() => payVia("stripe")}
              disabled={!!paying}
              className="group flex items-center gap-4 rounded-2xl border-2 border-indigo-100 bg-gradient-to-br from-indigo-50/60 via-white to-violet-50/40 p-4 text-left hover:border-indigo-400 hover:shadow-md transition disabled:opacity-50"
              data-testid="pay-method-stripe-btn"
            >
              <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white flex items-center justify-center shadow-md shrink-0">
                {paying === "stripe" ? <Loader2 className="h-5 w-5 animate-spin" /> : <CreditCard className="h-5 w-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">Pay with card · Stripe</div>
                <div className="text-xs text-neutral-500 mt-0.5">Visa · Mastercard · Amex. Secure Stripe-hosted checkout.</div>
              </div>
              <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0" />
            </button>
            <button
              type="button"
              onClick={() => payVia("paysera")}
              disabled={!!paying}
              className="group flex items-center gap-4 rounded-2xl border-2 border-amber-100 bg-gradient-to-br from-amber-50/60 via-white to-orange-50/40 p-4 text-left hover:border-amber-400 hover:shadow-md transition disabled:opacity-50"
              data-testid="pay-method-paysera-btn"
            >
              <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 text-white flex items-center justify-center shadow-md shrink-0">
                {paying === "paysera" ? <Loader2 className="h-5 w-5 animate-spin" /> : <Wallet className="h-5 w-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">Pay via Paysera</div>
                <div className="text-xs text-neutral-500 mt-0.5">Bank transfer · card · e-money. Hosted by Paysera.</div>
              </div>
              <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0" />
            </button>
          </div>
          {paying && (
            <div className="text-xs text-neutral-500 text-center mt-2">
              <Loader2 className="inline h-3 w-3 mr-1 animate-spin" />
              Redirecting to {paying === "stripe" ? "Stripe" : "Paysera"}…
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
